"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EspoolConfiguration = void 0;
const echain_shared_articles_oo_1 = require("@igus/echain-shared-articles-oo");
const echain_shared_articles_1 = require("@igus/echain-shared-articles");
const Configuration_1 = require("./Configuration");
const echain_shared_configurations_1 = require("@igus/echain-shared-configurations");
/**
 * An `EspoolConfigurationModel` accessor with basic utility methods.
 */
class EspoolConfiguration {
    get espool() {
        return (0, echain_shared_articles_oo_1.A)(this.model.Espool);
    }
    get splitPositionX() {
        return this.model.SplitPositionX;
    }
    static fromModel(model) {
        return new EspoolConfiguration(model);
    }
    static fromConfiguration(configuration) {
        const configurationModel = configuration.toModel();
        if (!(0, echain_shared_configurations_1.isEspoolConfigurationModel)(configurationModel)) {
            throw new Error(`${EspoolConfiguration.name}.fromConfiguration(): not an espool model`);
        }
        return this.fromModel(configurationModel);
    }
    static fromConfigurationIfEspool(configuration) {
        const configurationModel = configuration.toModel();
        if (!(0, echain_shared_configurations_1.isEspoolConfigurationModel)(configurationModel)) {
            return undefined;
        }
        return this.fromModel(configurationModel);
    }
    constructor(model) {
        this.model = model;
        this.className = 'EspoolConfiguration';
    }
    toModel() {
        return this.model;
    }
    hasLeftChild() {
        return this.model.Children.some((c) => c.ChildRelation.Position === echain_shared_articles_1.EspoolChainPosition.left);
    }
    hasRightChild() {
        return this.model.Children.some((c) => c.ChildRelation.Position === echain_shared_articles_1.EspoolChainPosition.right);
    }
    /**
     * Retrieves a Configuration for each position.
     */
    getPositionedChildren() {
        const linear = this.getChildByPosition(echain_shared_articles_1.EspoolChainPosition.linear);
        if (!linear) {
            throw new Error(`${this.className}.getPositionedChildren() linear child is undefined`);
        }
        const left = this.getChildByPosition(echain_shared_articles_1.EspoolChainPosition.left);
        const right = this.getChildByPosition(echain_shared_articles_1.EspoolChainPosition.right);
        if (left && right) {
            return {
                linear,
                left,
                right,
            };
        }
        if (left) {
            return {
                linear,
                left,
                right: undefined,
            };
        }
        if (right) {
            return {
                linear,
                left: undefined,
                right,
            };
        }
        throw new Error(`${this.className}.getPositionedChildren() left and right children are undefined`);
    }
    /**
     * Retrieves a Configuration for each position.
     */
    getPositionedChildModels() {
        const linear = this.getChildModelByPosition(echain_shared_articles_1.EspoolChainPosition.linear);
        if (!linear) {
            throw new Error(`${this.className}.getPositionedChildModels() linear child is undefined`);
        }
        const left = this.getChildModelByPosition(echain_shared_articles_1.EspoolChainPosition.left);
        const right = this.getChildModelByPosition(echain_shared_articles_1.EspoolChainPosition.right);
        if (left && right) {
            return {
                linear,
                left,
                right,
            };
        }
        if (left) {
            return {
                linear,
                left,
                right: undefined,
            };
        }
        if (right) {
            return {
                linear,
                left: undefined,
                right,
            };
        }
        throw new Error(`${this.className}.getPositionedChildren() left and right children are undefined`);
    }
    /**
     * Retrieves a Configuration for a dedicated position.
     */
    getChildByPosition(espoolChainPosition) {
        const child = this.getChildModelByPosition(espoolChainPosition);
        if (child) {
            return Configuration_1.Configuration.fromModel(child);
        }
        return undefined;
    }
    /**
     * Retrieves a ConfigurationModel for a dedicated position.
     */
    getChildModelByPosition(espoolChainPosition) {
        const artNrErsatzes = this.getArtNrErsatzes();
        if (artNrErsatzes[espoolChainPosition]) {
            return this.model.Children.find((c) => {
                const childRelation = c.ChildRelation;
                return (childRelation === null || childRelation === void 0 ? void 0 : childRelation.Position) === espoolChainPosition;
            });
        }
        return undefined;
    }
    /**
     * Retrieves a Configuration for every child in the model.
     */
    getChildren() {
        return this.model.Children.map((c) => Configuration_1.Configuration.fromModel(c));
    }
    /**
     * Retrieves all Leitungen having a linear position together with their left or right sibling.
     *  example for espool having left and right
     *    linear: L1, L2
     *    left: L1
     *    right: L2
     *  example for espool having only left
     *    linear: L1, L2
     *    left: L1, L2
     */
    getChildLeitungenByLinearPositionSortedFromOuterToInner() {
        const children = this.getPositionedChildren();
        const placedLinearLeitungen = children.linear.leitungen.getPlacedLeitungList().clone();
        if (children.left && !children.right) {
            return {
                linear: placedLinearLeitungen,
                left: placedLinearLeitungen.getSortedByLocalTransformXAsc(),
                right: undefined,
            };
        }
        if (!children.left && children.right) {
            return {
                linear: placedLinearLeitungen,
                left: undefined,
                right: placedLinearLeitungen.getSortedByLocalTransformXDesc(),
            };
        }
        const splitX = this.splitPositionX;
        const left = placedLinearLeitungen.getSortedByLocalTransformXAsc().filter((l) => l.localTransformPosition.x < splitX);
        const right = placedLinearLeitungen.getSortedByLocalTransformXDesc().filter((l) => l.localTransformPosition.x >= splitX);
        return {
            linear: placedLinearLeitungen,
            left,
            right,
        };
    }
    getKette(espoolChainPosition) {
        var _a;
        return (_a = this.getChildByPosition(espoolChainPosition)) === null || _a === void 0 ? void 0 : _a.getKetteClone();
    }
    getIAsPositioned() {
        var _a, _b, _c;
        return {
            linear: (_a = this.getChildByPosition(echain_shared_articles_1.EspoolChainPosition.linear)) === null || _a === void 0 ? void 0 : _a.ias,
            left: (_b = this.getChildByPosition(echain_shared_articles_1.EspoolChainPosition.left)) === null || _b === void 0 ? void 0 : _b.ias,
            right: (_c = this.getChildByPosition(echain_shared_articles_1.EspoolChainPosition.right)) === null || _c === void 0 ? void 0 : _c.ias,
        };
    }
    getIAs() {
        return echain_shared_articles_oo_1.IAList.fromArray(this.getChildren().reduce((iaArr, child) => {
            return iaArr.concat(child.getIAsClone().toArray());
        }, []));
    }
    getArtNrErsatzes() {
        var _a, _b, _c;
        if (!this.model.Espool.LinearKette) {
            throw new Error(`${this.className}.getSideConfigurationByLeitungInLinear() this.model.Espool.LinearKette is undefined`);
        }
        return {
            linear: (_a = this.model.Espool.LinearKette) === null || _a === void 0 ? void 0 : _a.Ref,
            left: (_b = this.model.Espool.LeftKette) === null || _b === void 0 ? void 0 : _b.Ref,
            right: (_c = this.model.Espool.RightKette) === null || _c === void 0 ? void 0 : _c.Ref,
        };
    }
    getBeatTextOptions(position) {
        var _a, _b, _c, _d;
        if (position === echain_shared_articles_1.EspoolChainPosition.linear) {
            const options = {
                position: echain_shared_articles_1.EspoolChainPosition.linear,
            };
            if (((_a = this.model.Espool.LinearKette) === null || _a === void 0 ? void 0 : _a.FixedPerNth) && ((_c = (_b = this.model.Espool.LinearKette) === null || _b === void 0 ? void 0 : _b.StrainReliefSeparatorRefs) === null || _c === void 0 ? void 0 : _c[0])) {
                options.replacement = {
                    perNth: this.model.Espool.LinearKette.FixedPerNth,
                    artNr: (_d = this.model.Espool.LinearKette.StrainReliefSeparatorRefs) === null || _d === void 0 ? void 0 : _d[0],
                };
            }
            return options;
        }
        return {
            position,
        };
    }
}
exports.EspoolConfiguration = EspoolConfiguration;
EspoolConfiguration.maxStackedLeitungenAtLinear = 1;
EspoolConfiguration.maxStackedLeitungenAtSide = 2;
//# sourceMappingURL=EspoolConfiguration.js.map